import React, { useEffect, useMemo, useState } from 'react';
import { Btn, DataTable, Loading, Msg, Section, fmtDate, panelGet, panelPost } from './kit';
import { Tag } from './accessKit';
import { useAdminAccess } from './adminAccess';

/**
 * /admin → Admins & access → Admin accounts and History. Every admin, team
 * accounts included, sees both; only super admins get the add / change /
 * remove controls (the server refuses everyone else's with 403).
 *
 * Who can sign in to the admin panel, as what, and who decided it. Two roles:
 * Admin (the groups Admin tabs opens to them) and Team (the same groups,
 * read-only: the server refuses every change a team account sends; Database
 * and System only when a super admin picks the account by name). Super admins
 * are fixed in code and cannot be changed here.
 *
 * Backend/admin_accounts.py through the panel dispatcher, section "admins":
 *   GET  admins/state     {accounts: [{email, name, has_account, role, active, super_admin, added_by,
 *                          added_at, last_change, last_sign_in, last_admin_action}], team_not_in_all}
 *   GET  admins/history   ?email= -> {events: [{key, at, email, event, role_before, role_after, by,
 *                          source, reason, recorded_in, ip?}]}
 *   POST admins/add       {email, role, reason}   (an inactive account is reactivated)
 *   POST admins/set_role  {email, role, reason}
 *   POST admins/remove    {email, reason}
 * Every change needs a reason; it is kept in the history.
 */

type Role = 'admin' | 'team';
const ROLE_LABEL: Record<Role, string> = { admin: 'Admin', team: 'Team (read-only)' };

interface Account {
  email: string;
  name: string;
  hasAccount: boolean;
  role: Role;
  active: boolean;
  superAdmin: boolean;
  addedBy: string;
  addedAt: string;
  lastChange: { event: string; role_before: string; role_after: string; by: string; at: string; reason: string } | null;
  lastSignIn: string;
  lastAdminAction: string;
}

const str = (v: any) => (typeof v === 'string' ? v : '');

function readAccounts(raw: any): Account[] {
  return (Array.isArray(raw) ? raw : []).filter((a: any) => a && typeof a.email === 'string').map((a: any) => ({
    email: a.email,
    name: str(a.name),
    hasAccount: a.has_account !== false,
    role: a.role === 'team' ? 'team' : 'admin',
    active: a.active !== false,
    superAdmin: a.super_admin === true,
    addedBy: str(a.added_by),
    addedAt: str(a.added_at),
    lastChange: a.last_change && typeof a.last_change === 'object'
      ? { event: str(a.last_change.event), role_before: str(a.last_change.role_before),
          role_after: str(a.last_change.role_after), by: str(a.last_change.by), at: str(a.last_change.at),
          reason: str(a.last_change.reason) }
      : null,
    lastSignIn: str(a.last_sign_in),
    lastAdminAction: str(a.last_admin_action),
  }));
}

/** One history row's "what happened", in words. */
export function eventText(e: { event: string; role_before?: string | null; role_after?: string | null }): string {
  const role = (r?: string | null) => (r === 'team' ? 'team (read-only)' : 'admin');
  switch (e.event) {
    case 'added': return e.role_after ? `Added as ${role(e.role_after)}` : 'Added as admin';
    case 'reactivated': return `Reactivated as ${role(e.role_after)}`;
    case 'role_changed': return `Role: ${role(e.role_before)} → ${role(e.role_after)}`;
    case 'removed': return 'Removed';
    case 'email_moved': return 'Moved with an account e-mail change';
    case 'seeded': return 'Added by the startup seed list';
    case 'first_added': return 'First on the admin list';
    case 'refused': return 'Attempt refused';
    default: return e.event;
  }
}

type Pending = { kind: 'remove' | 'role' | 'reactivate'; account: Account; role: Role };

export const AdminAccounts: React.FC<{ onHistory: (email: string) => void }> = ({ onHistory }) => {
  const { superAdmin } = useAdminAccess();
  const [st, setSt] = useState<{ loading: boolean; error: string; data: any }>({ loading: true, error: '', data: null });
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('team');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [pendingReason, setPendingReason] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const load = async () => {
    setSt(s => ({ ...s, loading: true, error: '' }));
    const { data, error } = await panelGet('admins', 'state');
    setSt(s => ({ loading: false, error, data: data ?? s.data }));
  };
  useEffect(() => { load(); }, []);

  const accounts = useMemo(() => readAccounts(st.data?.accounts), [st.data]);
  const notInAll: string[] = Array.isArray(st.data?.team_not_in_all) ? st.data.team_not_in_all.map(String) : [];
  const shown = accounts.filter(a => showInactive || a.active);
  const counts = {
    admin: accounts.filter(a => a.active && a.role === 'admin').length,
    team: accounts.filter(a => a.active && a.role === 'team').length,
    inactive: accounts.filter(a => !a.active).length,
  };

  const add = async () => {
    setBusy(true);
    const { data, error } = await panelPost('admins', 'add', { email: email.trim(), role, reason: reason.trim() });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: `${data?.email || email} ${data?.event === 'reactivated' ? 'is back' : 'is now'} `
      + `${role === 'team' ? 'a read-only team account' : 'an admin'}. They sign in with their normal Cosmos login.` });
    setEmail(''); setReason('');
    load();
  };

  const confirm = async () => {
    if (!pending) return;
    const { kind, account } = pending;
    setBusy(true);
    const body = { email: account.email, reason: pendingReason.trim(),
      ...(kind === 'remove' ? {} : { role: pending.role }) };
    const action = kind === 'remove' ? 'remove' : kind === 'role' ? 'set_role' : 'add';
    const { error } = await panelPost('admins', action, body);
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: `${account.email}: ${error}` }); return; }
    setMsg({ kind: 'ok', text: kind === 'remove' ? `${account.email} can no longer open the admin panel.`
      : `${account.email} is now ${pending.role === 'team' ? 'a read-only team account' : 'an admin'}.` });
    setPending(null); setPendingReason('');
    load();
  };

  const pendingTitle = pending
    ? pending.kind === 'remove' ? `Remove ${pending.account.email}`
      : pending.kind === 'role' ? `Make ${pending.account.email} ${ROLE_LABEL[pending.role].toLowerCase()}`
        : `Reactivate ${pending.account.email} as ${ROLE_LABEL[pending.role].toLowerCase()}`
    : '';

  return (
    <div className="flex flex-col gap-4">
      {superAdmin && <Section title="Add an admin or team member">
        <p className="text-xs text-grayscale-1100 -mt-1">
          They sign in with their normal Cosmos login. <b>Admin</b>: the groups Admin tabs opens to admins.{' '}
          <b>Team (read-only)</b>: the same groups to look at, but every change is refused
          {notInAll.length ? <> ({notInAll.map(g => g.charAt(0).toUpperCase() + g.slice(1)).join(' and ')} only when
            you pick them by name in Admin tabs)</> : null}. Only super admins can add, change or remove accounts,
          and every change is kept in History.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="name@company.com"
            aria-label="E-mail address"
            className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-full sm:w-64" />
          <select value={role} onChange={e => setRole(e.target.value as Role)} aria-label="Role"
            className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
            <option value="team">{ROLE_LABEL.team}</option>
            <option value="admin">{ROLE_LABEL.admin}</option>
          </select>
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why (kept in the history)"
            aria-label="Reason"
            className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm flex-1 min-w-[12rem]" />
          <Btn kind="primary" disabled={busy || !email.trim() || reason.trim().length < 3} onClick={add}
            title={reason.trim().length < 3 ? 'Say why first' : undefined}>
            {busy ? 'Saving…' : role === 'team' ? 'Add team member' : 'Add admin'}
          </Btn>
        </div>
      </Section>}

      <Section title="Admin accounts"
        right={<span className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-grayscale-1200">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show removed ({counts.inactive})
          </label>
          <Btn small onClick={load} disabled={st.loading}>Refresh</Btn>
        </span>}>
        {!superAdmin && <p className="text-xs text-grayscale-1100 -mt-1">Only super admins can change admins.</p>}
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Tag tone="green">{counts.admin} admin{counts.admin === 1 ? '' : 's'}</Tag>
          <Tag tone="amber">{counts.team} team (read-only)</Tag>
          {counts.inactive > 0 && <Tag tone="grey">{counts.inactive} removed</Tag>}
        </div>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        {st.error && <Msg kind="error">{st.data ? `Could not refresh: ${st.error}` : st.error}</Msg>}
        {pending && (
          <div className="flex flex-col gap-2 rounded-lg border border-primary-200 bg-primary-50 p-3">
            <span className="text-sm font-semibold text-grayscale-1300">{pendingTitle}</span>
            <div className="flex items-center gap-2 flex-wrap">
              <input autoFocus value={pendingReason} onChange={e => setPendingReason(e.target.value)}
                placeholder="Why (kept in the history)" aria-label="Reason"
                className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm flex-1 min-w-[12rem]" />
              <Btn small kind={pending.kind === 'remove' ? 'danger' : 'primary'}
                disabled={busy || pendingReason.trim().length < 3} onClick={confirm}>
                {busy ? 'Saving…' : 'Confirm'}
              </Btn>
              <Btn small disabled={busy} onClick={() => { setPending(null); setPendingReason(''); }}>Cancel</Btn>
            </div>
          </div>
        )}
        {st.loading && !st.data ? <Loading /> : (
          <DataTable rows={shown as any[]} tableId="admin_accounts" csvName="admin_accounts"
            columns={['email', 'role', 'addedBy', 'lastChange', 'lastSignIn', 'lastAdminAction']}
            labels={{ email: 'Account', role: 'Role', addedBy: 'Added', lastChange: 'Last change',
              lastSignIn: 'Last sign-in', lastAdminAction: 'Last admin change made' }}
            unsortable={['lastChange']}
            empty="No admin accounts."
            render={{
              email: (a: any) => (
                <span className="flex flex-col">
                  <span className="flex items-center gap-1.5 flex-wrap">
                    <span className={a.active ? 'text-grayscale-1300' : 'text-grayscale-1100 line-through'}>
                      {a.name || a.email}
                    </span>
                    {a.superAdmin && <Tag tone="violet">Super admin</Tag>}
                    {!a.active && <Tag tone="grey">Removed</Tag>}
                    {!a.hasAccount && <Tag tone="grey" title="No Cosmos account with this address yet">No account yet</Tag>}
                  </span>
                  {a.name && <span className="text-[11px] text-grayscale-1100">{a.email}</span>}
                </span>
              ),
              role: (a: any) => (a.role === 'team'
                ? <Tag tone="amber">Team · read-only</Tag> : <Tag tone="green">Admin</Tag>),
              addedBy: (a: any) => (
                <span className="flex flex-col text-xs">
                  <span>{a.addedBy || '—'}</span>
                  {a.addedAt && <span className="text-grayscale-1100">{fmtDate(a.addedAt)}</span>}
                </span>
              ),
              lastChange: (a: any) => (a.lastChange ? (
                <span className="flex flex-col text-xs max-w-[16rem]">
                  <span>{eventText(a.lastChange)} · {a.lastChange.by}</span>
                  <span className="text-grayscale-1100">{fmtDate(a.lastChange.at)}</span>
                  {a.lastChange.reason && <span className="text-grayscale-1100 line-clamp-2">“{a.lastChange.reason}”</span>}
                </span>
              ) : <span className="text-xs text-grayscale-1100">—</span>),
              lastSignIn: (a: any) => <span className="text-xs">{fmtDate(a.lastSignIn)}</span>,
              lastAdminAction: (a: any) => <span className="text-xs">{fmtDate(a.lastAdminAction)}</span>,
            }}
            actions={(row: any) => {
              const a = row as Account;
              return (
                <span className="flex items-center gap-1 justify-end flex-wrap">
                  <Btn small onClick={() => onHistory(a.email)}>History</Btn>
                  {superAdmin && !a.superAdmin && a.active && (
                    <Btn small disabled={busy} onClick={() => {
                      setPending({ kind: 'role', account: a, role: a.role === 'team' ? 'admin' : 'team' });
                      setPendingReason('');
                    }}>{a.role === 'team' ? 'Make admin' : 'Make read-only'}</Btn>
                  )}
                  {superAdmin && !a.superAdmin && a.active && (
                    <Btn small kind="danger" disabled={busy} onClick={() => {
                      setPending({ kind: 'remove', account: a, role: a.role }); setPendingReason('');
                    }}>Remove</Btn>
                  )}
                  {superAdmin && !a.superAdmin && !a.active && (
                    <Btn small disabled={busy} onClick={() => {
                      setPending({ kind: 'reactivate', account: a, role: a.role }); setPendingReason('');
                    }}>Reactivate</Btn>
                  )}
                </span>
              );
            }} />
        )}
      </Section>
    </div>
  );
};

export const AdminHistory: React.FC<{ email: string; onEmail: (email: string) => void }> = ({ email, onEmail }) => {
  const [q, setQ] = useState(email);
  const [st, setSt] = useState<{ loading: boolean; error: string; rows: any[] }>({ loading: true, error: '', rows: [] });
  useEffect(() => { setQ(email); }, [email]);
  useEffect(() => {
    let dead = false;
    setSt(s => ({ ...s, loading: true, error: '' }));
    (async () => {
      const { data, error } = await panelGet('admins', 'history', email ? { email, limit: 1000 } : { limit: 1000 });
      if (dead) return;
      setSt(s => ({ loading: false, error, rows: Array.isArray(data?.events) ? data.events : s.rows }));
    })();
    return () => { dead = true; };
  }, [email]);

  const rows = useMemo(() => st.rows.map((e: any) => ({
    ...e, what: eventText(e), at: e.at || '', reason: e.reason || '', ip: e.ip || '',
  })), [st.rows]);

  return (
    <Section title="Admin history"
      right={<span className="flex items-center gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by account e-mail"
          aria-label="Filter by account e-mail"
          onKeyDown={e => { if (e.key === 'Enter') onEmail(q.trim().toLowerCase()); }}
          className="h-8 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-full sm:w-60" />
        <Btn small onClick={() => onEmail(q.trim().toLowerCase())}>Show</Btn>
        {email && <Btn small onClick={() => onEmail('')}>All accounts</Btn>}
      </span>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Every time someone was made an admin or team member, changed role or removed, and who did it. Changes made
        before 29 Oct 2026 come from the audit log (the Billing tab's Add admin and Remove, recorded since 20 Sep 2026)
        and from the admin list's own “added by”.
      </p>
      {st.error && <Msg kind="error">{st.error}</Msg>}
      {st.loading && !st.rows.length ? <Loading /> : (
        <DataTable rows={rows} tableId="admin_history" csvName={email ? `admin_history_${email}` : 'admin_history'}
          columns={['at', 'email', 'what', 'by', 'reason', 'source', 'recorded_in']}
          optionalColumns={['ip']}
          labels={{ at: 'When', email: 'Account', what: 'What happened', by: 'By', reason: 'Reason',
            source: 'Where', recorded_in: 'Recorded in', ip: 'IP' }}
          defaultSort="-at" searchable
          empty={email ? `No recorded changes for ${email}.` : 'No recorded changes.'}
          render={{
            at: (r: any) => <span className="text-xs whitespace-nowrap">{fmtDate(r.at)}</span>,
            email: (r: any) => (
              <button type="button" className="text-left text-primary-700 hover:underline"
                onClick={() => onEmail(r.email)}>{r.email}</button>
            ),
            reason: (r: any) => <span className="text-xs text-grayscale-1200">{r.reason || '—'}</span>,
          }} />
      )}
    </Section>
  );
};
